/**
 * Curated medieval-to-20th-c. Antwerp landmarks, folklore sites, and painting
 * scenes for the Wimmelbild illustrator's brief. Distinct from Onroerend
 * Erfgoed records (`Landmark`) — these are story-rich, hand-picked points
 * with imagery and artist-direction notes.
 */

export type AntwerpLandmarkCategory =
  | 'folklore'
  | 'fortification'
  | 'castle'
  | 'church'
  | 'abbey'
  | 'market'
  | 'palace'
  | 'house'
  | 'guild'
  | 'almshouse'
  | 'demolished'
  | 'red-light'
  | 'tavern'
  | 'painter'
  | 'monument'
  // Artist-brief categories — used mostly for custom POIs, but legal on
  // curated entries too.
  | 'scene'
  | 'character'
  | 'object'
  | 'event'
  | 'detail';

/** All categories ordered by typical population — single source of truth for legend, panel, map. */
export const ANTWERP_CATEGORIES: ReadonlyArray<AntwerpLandmarkCategory> = [
  'folklore',
  'monument',
  'market',
  'demolished',
  'guild',
  'house',
  'painter',
  'almshouse',
  'church',
  'fortification',
  'castle',
  'palace',
  'tavern',
  'red-light',
  'abbey',
  'scene',
  'character',
  'object',
  'event',
  'detail',
];

export const ANTWERP_CATEGORY_LABEL: Record<AntwerpLandmarkCategory, string> = {
  folklore: 'Folklore',
  fortification: 'Fortification',
  castle: 'Castle / château',
  church: 'Church',
  abbey: 'Abbey',
  market: 'Market / square',
  palace: 'Palace',
  house: 'Notable house',
  guild: 'Guild building',
  almshouse: 'Almshouse',
  demolished: 'Demolished',
  'red-light': 'Historic red-light',
  tavern: 'Tavern / inn',
  painter: 'Painter',
  monument: 'Monument',
  scene: 'Scene to depict',
  character: 'Character',
  object: 'Object / prop',
  event: 'Event',
  detail: 'Detail / atmosphere',
};

export const ANTWERP_CATEGORY_COLOR: Record<AntwerpLandmarkCategory, string> = {
  folklore: '#a020f0',
  fortification: '#5d4037',
  castle: '#7c4a1e',
  church: '#8b3a2a',
  abbey: '#5d2e8c',
  market: '#388e3c',
  palace: '#1976d2',
  house: '#0288d1',
  guild: '#6d4c41',
  almshouse: '#7b1fa2',
  demolished: '#9e9e9e',
  'red-light': '#c2185b',
  tavern: '#e65100',
  painter: '#f9a825',
  monument: '#455a64',
  scene: '#00897b',
  character: '#d81b60',
  object: '#827717',
  event: '#bf360c',
  detail: '#7e57c2',
};

export interface AntwerpLandmarkPainting {
  artist: string;
  title: string;
  year?: string;
  /** Wikimedia / museum URL for the painting image. */
  url: string;
}

export interface AntwerpLandmarkImage {
  src: string;
  caption: string;
  source?: string;
}

export interface AntwerpLandmark {
  id: string;
  name: string;
  alternativeNames?: string[];
  category: AntwerpLandmarkCategory;
  /** Free-form period: "13th c." | "1500-1565" | "demolished 1864". */
  period: string;
  /** [longitude, latitude] WGS84. */
  coordinates: [number, number];
  /** One-sentence essence — used in legends and tooltips. */
  oneLiner: string;
  /** 2-4 sentence narrative for the panel. */
  description: string;
  /** Artist-briefing note: what to draw, mood, distinctive features. */
  artistDirection?: string;
  /** Story or myth associated with the place. */
  folklore?: string;
  /** Famous artworks depicting this place. */
  paintings?: AntwerpLandmarkPainting[];
  /** Photographs / engravings the artist can copy from. */
  visualReferences?: AntwerpLandmarkImage[];
  /** Whether the building / monument still physically exists. */
  stillStands: boolean;
  wikipedia?: string;
}

export interface AntwerpLandmarkCatalogue {
  version: string;
  records: AntwerpLandmark[];
}
